import { createHash } from "node:crypto";
import { accessSync, constants } from "node:fs";
import { access, readFile, realpath } from "node:fs/promises";
import { homedir, tmpdir } from "node:os";
import { delimiter, dirname, extname, isAbsolute, join } from "node:path";
import { runCliDriver } from "./drivers/cli-driver.js";
const ENV_NODE_SHEBANG = "#!/usr/bin/env node";
const WINDOWS_PATHEXT = [".com", ".exe", ".bat", ".cmd"];
const SHEBANG_ARGS_SPLIT_RE = /\s+/;
const FIRST_LINE_SPLIT_RE = /\r?\n/u;
const NODE_VERSION_PATH_RE = /(?:^|\/)v?(\d+\.\d+\.\d+)(?:\/|$)/u;
const MACOS_TEMP_FOLDER_RE = /^\/var\/folders\/[^/]+\/[^/]+\/t(?:\/|$)/u;
const WRAPPER_PATH_SEGMENT_RE = /(?:^|[-_.])(?:shim|shims|wrapper|wrappers)(?:$|[-_.])/u;
const HOME_DIR = normalizeOptionalText(homedir());
const TEMP_DIR = normalizeOptionalText(tmpdir());
export function buildExecutableLaunchContract(input) {
    return {
        kind: "executable",
        commandPath: input.commandPath,
        ...(input.argsPrefix && input.argsPrefix.length > 0
            ? { argsPrefix: [...input.argsPrefix] }
            : {}),
    };
}
export function materializeCliLaunchCommand(input) {
    if (input.launchContract.kind === "executable") {
        const shell = commandPathRequiresShell(input.launchContract.commandPath);
        return {
            argsPrefix: [...(input.launchContract.argsPrefix ?? [])],
            bin: input.launchContract.commandPath,
            ...(shell ? { shell: true } : {}),
        };
    }
    return {
        argsPrefix: [
            input.launchContract.scriptPath,
            ...(input.launchContract.argsPrefix ?? []),
        ],
        bin: input.launchContract.nodePath,
    };
}
export function buildCliLaunchInvocation(input) {
    const launchCommand = materializeCliLaunchCommand({
        launchContract: input.launchContract,
    });
    return {
        args: [...launchCommand.argsPrefix, ...input.args],
        bin: launchCommand.bin,
        ...(launchCommand.shell ? { shell: true } : {}),
    };
}
export async function verifyLaunchContractFiles(input) {
    try {
        const launchCommand = materializeCliLaunchCommand(input);
        if (input.launchContract.kind === "executable") {
            await assertExecutablePath(input.launchContract.commandPath);
        }
        else {
            await assertExecutablePath(input.launchContract.nodePath);
            await assertReadablePath(input.launchContract.scriptPath);
        }
        return {
            launchCommand,
            ok: true,
        };
    }
    catch (error) {
        return {
            errorMessage: toErrorMessage(error),
            ok: false,
        };
    }
}
export async function verifyCliLaunchContract(input) {
    const fileCheck = await verifyLaunchContractFiles({
        launchContract: input.launchContract,
    });
    if (!fileCheck.ok) {
        return {
            errorMessage: `${input.displayName} launch contract is broken on this device: ${fileCheck.errorMessage}`,
            ok: false,
        };
    }
    try {
        const invocation = buildCliLaunchInvocation({
            args: input.probeArgs,
            launchContract: input.launchContract,
        });
        await runCliDriver({
            allowNonZeroExit: false,
            args: invocation.args,
            bin: invocation.bin,
            cwd: input.cwd,
            env: input.env,
            timeoutMs: input.timeoutMs ?? 15_000,
        });
        return fileCheck;
    }
    catch (error) {
        return {
            errorMessage: `${input.displayName} can't start on this device: ${toErrorMessage(error)}`,
            ok: false,
        };
    }
}
export async function resolveInstalledNodeScriptLaunchContract(input) {
    const candidates = await resolveInstalledNodeScriptLaunchCandidates({
        commandName: input.commandName,
        configuredCommand: input.configuredCommand ?? null,
        displayName: input.displayName,
    });
    if (!candidates.ok) {
        return candidates;
    }
    const candidate = candidates.candidates[0] ?? null;
    if (!candidate) {
        return {
            errorMessage: `${input.displayName} is not installed on this device.`,
            ok: false,
        };
    }
    return {
        commandPath: candidate.commandPath,
        launchContract: candidate.launchContract,
        ok: true,
    };
}
export async function resolveInstalledNodeScriptLaunchCandidates(input) {
    const commandPaths = resolveInstalledCommandPaths({
        commandName: input.commandName,
        configuredCommand: input.configuredCommand ?? null,
    });
    if (commandPaths.length === 0) {
        return {
            errorMessage: `${input.displayName} is not installed on this device.`,
            ok: false,
        };
    }
    const candidates = [];
    const candidateIds = new Set();
    const inspectionErrors = [];
    for (const commandPath of commandPaths) {
        const candidate = await inspectInstalledNodeScriptLaunchCandidate({
            commandPath,
            displayName: input.displayName,
        });
        if (!candidate.ok) {
            inspectionErrors.push(candidate.errorMessage);
            continue;
        }
        if (candidateIds.has(candidate.launchCandidate.installId)) {
            continue;
        }
        candidateIds.add(candidate.launchCandidate.installId);
        candidates.push(candidate);
    }
    if (candidates.length === 0) {
        return {
            errorMessage: inspectionErrors[0] ??
                `${input.displayName} is installed, but WakeLoop could not inspect its launch files.`,
            ok: false,
        };
    }
    const stableCandidates = candidates.filter((candidate) => !candidate.transientWrapper);
    const selectedCandidates = stableCandidates.length > 0 ? stableCandidates : candidates;
    return {
        candidates: finalizeLaunchCandidateDisplayMetadata(selectedCandidates.map((candidate) => candidate.launchCandidate)),
        ok: true,
    };
}
async function inspectInstalledNodeScriptLaunchCandidate(input) {
    try {
        await assertExecutablePath(input.commandPath);
        const scriptPath = await realpath(input.commandPath);
        const script = await readLaunchScriptInspection(scriptPath);
        const nodePath = await resolvePinnedNodePath({
            commandPath: scriptPath,
            firstLine: script.firstLine,
        });
        const transientWrapper = isTransientPathWrapper({
            commandPath: input.commandPath,
            contents: script.contents,
            firstLine: script.firstLine,
            scriptPath,
        });
        const launchContract = nodePath
            ? {
                kind: "node_script",
                nodePath,
                scriptPath,
            }
            : buildExecutableLaunchContract({
                commandPath: input.commandPath,
            });
        return {
            launchCandidate: buildLaunchCandidate({
                commandPath: input.commandPath,
                launchContract,
                transientWrapper,
            }),
            ok: true,
            transientWrapper,
        };
    }
    catch (error) {
        return {
            errorMessage: `${input.displayName} is installed, but WakeLoop could not inspect its launch files: ${toErrorMessage(error)}`,
            ok: false,
        };
    }
}
export function resolveInstalledCommandPath(input) {
    return resolveInstalledCommandPaths(input)[0] ?? null;
}
export function resolveInstalledCommandPaths(input) {
    const configuredCommand = normalizeOptionalText(input.configuredCommand);
    if (configuredCommand) {
        if (isAbsolute(configuredCommand)) {
            return [configuredCommand];
        }
        if (configuredCommand.includes("/") || configuredCommand.includes("\\")) {
            return [];
        }
        return resolveExecutablePathsOnPath(configuredCommand);
    }
    return resolveExecutablePathsOnPath(input.commandName);
}
export function resolveExecutableOnPath(commandName) {
    return resolveExecutablePathsOnPath(commandName)[0] ?? null;
}
export function resolveExecutablePathsOnPath(commandName) {
    const normalizedCommandName = normalizeOptionalText(commandName);
    if (!normalizedCommandName) {
        return [];
    }
    const pathValue = normalizeOptionalText(process.env.PATH);
    if (!pathValue) {
        return [];
    }
    const resolvedPaths = [];
    for (const pathEntry of pathValue.split(delimiter)) {
        const trimmedEntry = pathEntry.trim();
        if (!trimmedEntry) {
            continue;
        }
        for (const candidateName of resolveCommandFileNameCandidates(normalizedCommandName)) {
            const candidate = join(trimmedEntry, candidateName);
            if (pathLooksExecutable(candidate) &&
                !resolvedPaths.includes(candidate)) {
                resolvedPaths.push(candidate);
            }
        }
    }
    return resolvedPaths;
}
export function resolveSiblingCommandPath(input) {
    const normalizedCommandName = normalizeOptionalText(input.commandName);
    if (!normalizedCommandName) {
        return null;
    }
    const siblingDirectory = dirname(input.anchorPath);
    for (const candidateName of resolveCommandFileNameCandidates(normalizedCommandName)) {
        const candidate = join(siblingDirectory, candidateName);
        if (pathLooksExecutable(candidate)) {
            return candidate;
        }
    }
    return null;
}
async function resolvePinnedNodePath(input) {
    const firstLine = normalizeOptionalText(input.firstLine);
    if (!firstLine) {
        return null;
    }
    if (firstLine === ENV_NODE_SHEBANG) {
        const nodeSibling = join(dirname(input.commandPath), "node");
        try {
            await assertExecutablePath(nodeSibling);
            return nodeSibling;
        }
        catch {
            return null;
        }
    }
    if (!firstLine.startsWith("#!")) {
        return null;
    }
    const interpreter = normalizeOptionalText(firstLine.slice(2));
    if (!(interpreter && isAbsolute(interpreter))) {
        return null;
    }
    const normalizedInterpreter = interpreter.split(SHEBANG_ARGS_SPLIT_RE, 1)[0] ?? interpreter;
    try {
        await assertExecutablePath(normalizedInterpreter);
        return normalizedInterpreter;
    }
    catch {
        return null;
    }
}
async function assertExecutablePath(path) {
    await access(path, resolveRunnablePathAccessMode());
}
async function assertReadablePath(path) {
    await access(path, constants.R_OK);
}
function pathLooksExecutable(path) {
    try {
        accessSync(path, resolveRunnablePathAccessMode());
        return true;
    }
    catch {
        return false;
    }
}
function commandPathRequiresShell(commandPath) {
    if (process.platform !== "win32") {
        return false;
    }
    const normalizedExtension = extname(commandPath).toLowerCase();
    return normalizedExtension === ".cmd" || normalizedExtension === ".bat";
}
function resolveCommandFileNameCandidates(commandName) {
    if (process.platform !== "win32") {
        return [commandName];
    }
    const normalizedExtension = extname(commandName).toLowerCase();
    if (normalizedExtension.length > 0) {
        return [commandName];
    }
    return [
        ...resolveWindowsPathExtensions(process.env.PATHEXT).map((extension) => `${commandName}${extension}`),
        commandName,
    ].filter((candidate, index, candidates) => {
        return candidates.indexOf(candidate) === index;
    });
}
function resolveRunnablePathAccessMode() {
    return process.platform === "win32" ? constants.F_OK : constants.X_OK;
}
function resolveWindowsPathExtensions(value) {
    const normalized = normalizeOptionalText(value);
    if (!normalized) {
        return [...WINDOWS_PATHEXT];
    }
    return normalized
        .split(";")
        .map((entry) => entry.trim().toLowerCase())
        .filter((entry, index, values) => entry && values.indexOf(entry) === index)
        .map((entry) => (entry.startsWith(".") ? entry : `.${entry}`));
}
function normalizeOptionalText(value) {
    const normalized = String(value ?? "").trim();
    return normalized.length > 0 ? normalized : null;
}
function buildLaunchCandidate(input) {
    const installId = buildLaunchCandidateInstallId(input.launchContract);
    const sourceLabel = input.transientWrapper
        ? "Temporary wrapper"
        : resolveLaunchCandidateSourceLabel(input.commandPath);
    const detail = resolveLaunchCandidateDetail({
        commandPath: input.commandPath,
        launchContract: input.launchContract,
        transientWrapper: input.transientWrapper,
    });
    return {
        installId,
        label: sourceLabel,
        detail,
        commandPath: input.commandPath,
        launchContract: input.launchContract,
    };
}
function buildLaunchCandidateInstallId(launchContract) {
    const canonical = JSON.stringify(canonicalizeLaunchContract(launchContract));
    const digest = createHash("sha256").update(canonical).digest("hex");
    return `install_${digest.slice(0, 12)}`;
}
function canonicalizeLaunchContract(launchContract) {
    if (launchContract.kind === "executable") {
        return {
            kind: launchContract.kind,
            commandPath: launchContract.commandPath,
            argsPrefix: [...(launchContract.argsPrefix ?? [])],
        };
    }
    return {
        kind: launchContract.kind,
        nodePath: launchContract.nodePath,
        scriptPath: launchContract.scriptPath,
        argsPrefix: [...(launchContract.argsPrefix ?? [])],
    };
}
function finalizeLaunchCandidateDisplayMetadata(candidates) {
    const candidateIndexesByLabel = new Map();
    for (const [index, candidate] of candidates.entries()) {
        const currentIndexes = candidateIndexesByLabel.get(candidate.label) ?? [];
        currentIndexes.push(index);
        candidateIndexesByLabel.set(candidate.label, currentIndexes);
    }
    const nextCandidates = candidates.map((candidate) => ({ ...candidate }));
    for (const indexes of candidateIndexesByLabel.values()) {
        if (indexes.length < 2) {
            continue;
        }
        const duplicateCandidates = indexes
            .map((index) => nextCandidates[index])
            .filter((candidate) => Boolean(candidate));
        const labelSuffixes = resolveDistinctLaunchCandidateLabelSuffixes(duplicateCandidates);
        for (const [offset, index] of indexes.entries()) {
            const suffix = labelSuffixes[offset];
            if (!suffix) {
                continue;
            }
            const currentCandidate = nextCandidates[index];
            if (!currentCandidate) {
                continue;
            }
            currentCandidate.label = `${currentCandidate.label} (${suffix})`;
        }
    }
    return nextCandidates;
}
function resolveDistinctLaunchCandidateLabelSuffixes(candidates) {
    const directoryLabels = candidates.map((candidate) => resolveLaunchCandidateDirectoryLabel(candidate.commandPath));
    if (hasOnlyDistinctValues(directoryLabels)) {
        return directoryLabels;
    }
    const commandLabels = candidates.map((candidate) => shortenPathForDisplay(candidate.commandPath));
    if (hasOnlyDistinctValues(commandLabels)) {
        return commandLabels;
    }
    return candidates.map((candidate) => candidate.installId);
}
function hasOnlyDistinctValues(values) {
    return values.length === new Set(values).size;
}
function resolveLaunchCandidateSourceLabel(commandPath) {
    const normalizedPath = commandPath.toLowerCase();
    if (normalizedPath.includes(".app/contents/")) {
        return "App install";
    }
    if (normalizedPath.includes("/.nvm/")) {
        return "nvm install";
    }
    if (normalizedPath.includes("/opt/homebrew/") ||
        normalizedPath.includes("/cellar/") ||
        normalizedPath.includes("/homebrew/")) {
        return "Homebrew install";
    }
    if (normalizedPath.includes("/.asdf/")) {
        return "asdf install";
    }
    if (normalizedPath.includes("/.volta/")) {
        return "Volta install";
    }
    if (normalizedPath.includes("/.fnm/")) {
        return "fnm install";
    }
    if (normalizedPath.includes("/.local/bin/")) {
        return "User install";
    }
    if (normalizedPath.includes("/usr/local/bin/")) {
        return "System-wide install";
    }
    if (normalizedPath.startsWith("/usr/bin/") ||
        normalizedPath.startsWith("/bin/")) {
        return "System install";
    }
    return "Other install";
}
function resolveLaunchCandidateDetail(input) {
    const nodeVersion = input.launchContract.kind === "node_script"
        ? resolveNodeVersionFromPath(input.launchContract.nodePath)
        : null;
    const commandDirectory = resolveLaunchCandidateDirectoryLabel(input.commandPath);
    if (input.transientWrapper) {
        return `Temporary PATH wrapper · ${commandDirectory}`;
    }
    if (nodeVersion) {
        return `Node ${nodeVersion} · ${commandDirectory}`;
    }
    return commandDirectory;
}
function resolveLaunchCandidateDirectoryLabel(commandPath) {
    return shortenPathForDisplay(dirname(commandPath));
}
function shortenPathForDisplay(path) {
    if (!(HOME_DIR && path.startsWith(HOME_DIR))) {
        return path;
    }
    if (path === HOME_DIR) {
        return "~";
    }
    return `~${path.slice(HOME_DIR.length)}`;
}
function resolveNodeVersionFromPath(path) {
    const match = path.match(NODE_VERSION_PATH_RE);
    return normalizeOptionalText(match?.[1]);
}
async function readLaunchScriptInspection(path) {
    const contents = await readFile(path, "utf8");
    const firstLine = contents.split(FIRST_LINE_SPLIT_RE, 1)[0] ?? "";
    return {
        contents,
        firstLine: normalizeOptionalText(firstLine),
    };
}
function isTransientPathWrapper(input) {
    if (!(isInsideTemporaryDirectory(input.commandPath) ||
        isInsideTemporaryDirectory(input.scriptPath))) {
        return false;
    }
    return (hasWrapperPathSegment(input.commandPath) ||
        hasWrapperPathSegment(input.scriptPath) ||
        shellScriptLooksLikeWrapper(input));
}
function isInsideTemporaryDirectory(path) {
    const normalizedPath = normalizePathForDetection(path);
    const normalizedTempDir = TEMP_DIR
        ? normalizePathForDetection(TEMP_DIR)
        : null;
    if (normalizedTempDir &&
        pathIsEqualOrDescendant(normalizedPath, normalizedTempDir)) {
        return true;
    }
    return (pathIsEqualOrDescendant(normalizedPath, "/tmp") ||
        pathIsEqualOrDescendant(normalizedPath, "/private/tmp") ||
        MACOS_TEMP_FOLDER_RE.test(normalizedPath));
}
function hasWrapperPathSegment(path) {
    return normalizePathForDetection(path)
        .split("/")
        .filter((segment) => segment.length > 0)
        .slice(-4)
        .some((segment) => WRAPPER_PATH_SEGMENT_RE.test(segment));
}
function shellScriptLooksLikeWrapper(input) {
    const firstLine = normalizeOptionalText(input.firstLine)?.toLowerCase() ?? "";
    if (!(firstLine.includes("bash") ||
        firstLine.includes("/sh") ||
        firstLine.includes("zsh"))) {
        return false;
    }
    const contents = input.contents.toLowerCase();
    return (contents.includes("wrapper_shim") ||
        contents.includes("shim_root") ||
        (contents.includes("\nexec ") &&
            (contents.includes("wrapper") || contents.includes("shim"))));
}
function normalizePathForDetection(path) {
    return path.replaceAll("\\", "/").replace(/\/+/gu, "/").toLowerCase();
}
function pathIsEqualOrDescendant(path, parentPath) {
    return path === parentPath || path.startsWith(`${parentPath}/`);
}
function toErrorMessage(error) {
    return error instanceof Error ? error.message : String(error);
}
